/**
 * Authentication Service - Handles authentication with Rails API
 * Uses secure token storage following security best practices
 */

import { fromRailsFormat } from '../models/User.js'
import { apiRequest, getApiUrl } from '../config/api.js'

const AUTH_STORAGE_KEY = 'dietedics_auth'

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} Current user object or null if not authenticated
 */
export async function getCurrentUser() {
  try {
    const data = await apiRequest('/users/current', {
      method: 'GET',
      includeAuth: true
    })
    return fromRailsFormat(data.user)
  } catch (error) {
    if (error.status === 401) {
      // Clear invalid auth
      clearAuth()
      return null
    }
    throw error
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated (synchronous check)
 */
export function isAuthenticated() {
  // Check if token is expired first
  if (isTokenExpired()) {
    clearAuth()
    return false
  }
  
  // Check if we have auth data in localStorage
  const authData = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!authData) {
    return false
  }
  
  try {
    const auth = JSON.parse(authData)
    return !!(auth.userId && auth.token)
  } catch (error) {
    clearAuth()
    return false
  }
}

/**
 * Check if current user is an admin
 * @returns {Promise<boolean>} True if current user is an admin
 */
export async function isAdmin() {
  try {
    const user = await getCurrentUser()
    return user?.isAdmin === true
  } catch (error) {
    return false
  }
}

/**
 * Check if current user is a provider user (has a provider_id)
 * @returns {Promise<boolean>} True if current user is a provider user
 */
export async function isProviderUser() {
  try {
    const user = await getCurrentUser()
    return !!(user?.providerId || user?.provider_id)
  } catch (error) {
    return false
  }
}

/**
 * Check if current user is an admin or provider user
 * @returns {Promise<boolean>} True if current user is an admin or provider user
 */
export async function isAdminOrProvider() {
  try {
    const user = await getCurrentUser()
    return user?.isAdmin === true || !!(user?.providerId || user?.provider_id)
  } catch (error) {
    return false
  }
}

/**
 * Verify authentication with the API (async)
 * @returns {Promise<boolean>} True if user is authenticated
 */
export async function verifyAuthentication() {
  // Check if token is expired first
  if (isTokenExpired()) {
    clearAuth()
    return false
  }
  
  // Verify token is still valid by checking current user
  try {
    const user = await getCurrentUser()
    return user !== null
  } catch (error) {
    return false
  }
}

/**
 * Sign up a new user
 * @param {Object} userData - User registration data
 * @param {string} userData.firstName - User first name
 * @param {string} userData.lastName - User last name
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 * @param {string} userData.city - User city
 * @param {string} userData.state - User state
 * @param {string} userData.experienceLevel - User experience level
 * @param {string} [userData.professionalRole] - Optional professional role (rdn or ndtr)
 * @param {string} [userData.licenseNumber] - Optional license number
 * @param {string} [userData.academyNumber] - Optional academy number
 * @param {string} [userData.providerId] - Optional provider ID for provider account signup
 * @returns {Promise<Object>} User object and auth token
 */
export async function signup(userData) {
  const url = getApiUrl('/users')
  
  // Convert to Rails format
  const railsData = {
    first_name: userData.firstName,
    last_name: userData.lastName,
    email: userData.email,
    password: userData.password,
    city: userData.city,
    state: userData.state,
    experience_level: userData.experienceLevel,
    professional_role: userData.professionalRole || null,
    license_number: userData.licenseNumber || null,
    academy_number: userData.academyNumber || null,
    provider_id: userData.providerId || null
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      user: railsData
    })
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Signup failed')
    error.status = response.status
    error.data = data
    throw error
  }

  // Extract user data from response
  const user = fromRailsFormat(data.user || data)
  
  // Store session indicator securely
  const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  
  storeAuthToken(user.id, sessionToken, expiresAt)
  
  return {
    user,
    token: sessionToken
  }
}

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User object and auth token
 */
export async function loginWithEmail(email, password) {
  const url = getApiUrl('/users/sign_in')
  
  // Devise expects user[email] and user[password] format
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include', // Important for cookies/sessions
    body: JSON.stringify({
      user: {
        email: email,
        password: password
      }
    })
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Login failed')
    error.status = response.status
    error.data = data
    throw error
  }

  // Extract user data from response
  // Devise returns user data in nested structure
  const user = fromRailsFormat(data.user || data)
  
  // For Devise with API mode, we're using session-based authentication
  // The session is stored in httpOnly cookies (more secure)
  // We'll store a session indicator in localStorage for client-side checks
  // The actual authentication is handled via cookies
  const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  
  // Store session indicator securely
  // The actual auth is in httpOnly cookies, which JavaScript cannot access
  // This is more secure than storing tokens in localStorage
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
  
  storeAuthToken(user.id, sessionToken, expiresAt)
  
  return {
    user,
    token: sessionToken
  }
}


// /**
//  * Login with Google SSO
//  * @param {Object} googleUser - Google user object from OAuth
//  * @returns {Promise<Object>} User object and auth token
//  */
// export async function loginWithGoogle(googleUser) {
//   try {
//     const url = getApiUrl('/users/auth/google_oauth2/callback')
    
//     // Send Google OAuth token to backend for verification
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//         'Accept': 'application/json'
//       },
//       credentials: 'include',
//       body: JSON.stringify({
//         provider: 'google_oauth2',
//         uid: googleUser.sub,
//         email: googleUser.email,
//         name: googleUser.name,
//         first_name: googleUser.given_name,
//         last_name: googleUser.family_name,
//         picture: googleUser.picture
//       })
//     })

//     const data = await response.json()

//     if (!response.ok) {
//       const error = new Error(data.message || data.error || 'Google login failed')
//       error.status = response.status
//       error.data = data
//       throw error
//     }

//     const user = fromRailsFormat(data.user || data)
//     // For OAuth, session is also stored in httpOnly cookies
//     const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
//     const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    
//     storeAuthToken(user.id, sessionToken, expiresAt)
    
//     return {
//       user,
//       token: sessionToken
//     }
//   } catch (error) {
//     throw error
//   }
// }

/**
 * Request password reset instructions
 * @param {string} email - User email address
 * @returns {Promise<void>}
 */
export async function requestPasswordReset(email) {
  const url = getApiUrl('/users/password')
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      user: {
        email: email
      }
    })
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Failed to send password reset instructions')
    error.status = response.status
    error.data = data
    throw error
  }

  return data
}

/**
 * Reset password with token
 * @param {string} token - Password reset token
 * @param {string} password - New password
 * @param {string} passwordConfirmation - Password confirmation
 * @returns {Promise<Object>} User object
 */
export async function resetPassword(token, password, passwordConfirmation) {
  const url = getApiUrl('/users/password')
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      user: {
        reset_password_token: token,
        password: password,
        password_confirmation: passwordConfirmation
      }
    })
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Failed to reset password')
    error.status = response.status
    error.data = data
    throw error
  }

  // Extract user data from response
  const user = fromRailsFormat(data.user || data)
  
  // Store session indicator securely
  const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
  
  storeAuthToken(user.id, sessionToken, expiresAt)
  
  return {
    user,
    token: sessionToken
  }
}

/**
 * Logout current user
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    const url = getApiUrl('/users/sign_out')
    
    // For session-based auth, we don't need to send a token
    // The session cookie is sent automatically with credentials: 'include'
    await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include' // Important: sends cookies
    })
  } catch (error) {
    // Even if the API call fails, clear local auth
    console.error('Error during logout:', error)
  } finally {
    // Always clear local auth data
    clearAuth()
  }
}

/**
 * Get auth token
 * @returns {string|null} Auth token or null
 */
export function getToken() {
  const authData = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!authData) return null
  
  try {
    const auth = JSON.parse(authData)
    
    // Check if token is expired
    if (auth.expiresAt && new Date(auth.expiresAt) < new Date()) {
      clearAuth()
      return null
    }
    
    return auth.token || null
  } catch (error) {
    clearAuth()
    return null
  }
}

/**
 * Check if auth token is expired
 * @returns {boolean} True if token is expired
 */
export function isTokenExpired() {
  const authData = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!authData) return true
  
  try {
    const auth = JSON.parse(authData)
    if (!auth.expiresAt) return true
    
    return new Date(auth.expiresAt) < new Date()
  } catch (error) {
    return true
  }
}

/**
 * Store authentication session indicator securely
 * Following security best practices: actual auth is in httpOnly cookies
 * This localStorage entry is just for client-side session tracking
 * @param {string} userId - User ID
 * @param {string} sessionToken - Session indicator (not the actual auth token)
 * @param {string} expiresAt - ISO 8601 expiration timestamp
 */
function storeAuthToken(userId, sessionToken, expiresAt) {
  if (!sessionToken) {
    console.warn('Attempted to store empty session token')
    return
  }
  
  const authData = {
    userId,
    token: sessionToken, // Session indicator, not actual auth token
    expiresAt,
    storedAt: new Date().toISOString()
  }
  
  // Store session indicator in localStorage
  // The actual authentication is handled via httpOnly cookies (more secure)
  // This localStorage entry is just for client-side convenience
  // httpOnly cookies cannot be accessed by JavaScript, protecting against XSS
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData))
  } catch (error) {
    console.error('Error storing auth session:', error)
    throw new Error('Failed to store authentication session')
  }
}

/**
 * Clear authentication data
 * Removes token from storage
 */
function clearAuth() {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing auth:', error)
  }
}

